import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Joystick, AlertTriangle, Radio, Terminal } from 'lucide-react';
import { fleet } from '../api/client';
import { StatusDot } from '../components/StatusDot';
import { TerminalPanel } from '../components/Terminal';
import { useAuth } from '../auth/AuthProvider';
import { FEATURE_FLAGS } from '../types';
import type { Device } from '../types';

type Tab = 'controls' | 'terminal';

export default function RemotePage() {
  const { user } = useAuth();
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('terminal');

  const hasRemote =
    user?.org_type === 'kinetica' ||
    user?.org_type === 'integrator' ||
    user?.features.includes(FEATURE_FLAGS.REMOTE_ACCESS);

  const { data: devices = [] } = useQuery({
    queryKey: ['fleet', 'devices'],
    queryFn: fleet.listDevices,
  });

  const selected = devices.find((a: Device) => a.serial === selectedDevice);

  if (!hasRemote) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Joystick className="text-gray-600 mb-4" size={48} />
        <h2 className="text-lg font-semibold text-white mb-2">Remote Operations</h2>
        <p className="text-sm text-gray-500 text-center max-w-md">
          Remote access requires Pro tier or the Remote Access add-on.
          Contact your integrator or upgrade your subscription to enable.
        </p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)]">
      <h2 className="text-lg font-semibold text-white mb-4">Remote Operations</h2>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 h-[calc(100%-3rem)]">
        {/* Device selector */}
        <div className="card lg:col-span-1">
          <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
            <Radio size={14} /> Select Device
          </h3>
          <div className="space-y-2">
            {devices.length === 0 ? (
              <p className="text-xs text-gray-500 py-4 text-center">No devices in fleet</p>
            ) : (
              devices.map((device: Device) => (
                <button
                  key={device.serial}
                  onClick={() => setSelectedDevice(device.serial)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    selectedDevice === device.serial
                      ? 'bg-kinetica-600/15 border border-kinetica-600/30'
                      : 'bg-surface-2 hover:bg-surface-3 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-gray-300">{device.serial}</span>
                    <StatusDot status={device.status} />
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{device.name}</div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Main panel */}
        <div className="lg:col-span-3 flex flex-col min-h-0">
          {!selected ? (
            <div className="card flex flex-col items-center justify-center py-12 text-center flex-1">
              <Terminal className="text-gray-600 mb-3" size={32} />
              <p className="text-sm text-gray-400">Select a device to begin remote operations</p>
              <p className="text-xs text-gray-600 mt-1">
                Terminal access and jog controls via MQTT through IoT Core
              </p>
            </div>
          ) : (
            <div className="flex flex-col flex-1 min-h-0">
              {/* Device info header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-white">{selected.name}</h3>
                    <span className="font-mono text-xs text-gray-500">{selected.serial}</span>
                  </div>
                  <StatusDot status={selected.status} showLabel size="md" />
                </div>
                <div className="flex items-center gap-1 bg-surface-2 rounded-lg p-0.5">
                  <button
                    onClick={() => setActiveTab('terminal')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      activeTab === 'terminal'
                        ? 'bg-surface-3 text-white'
                        : 'text-gray-500 hover:text-gray-300'
                    }`}
                  >
                    <Terminal size={12} />
                    Terminal
                  </button>
                  <button
                    onClick={() => setActiveTab('controls')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      activeTab === 'controls'
                        ? 'bg-surface-3 text-white'
                        : 'text-gray-500 hover:text-gray-300'
                    }`}
                  >
                    <Joystick size={12} />
                    Controls
                  </button>
                </div>
              </div>

              {/* Tab content */}
              {activeTab === 'terminal' ? (
                <div className="flex-1 min-h-0">
                  <TerminalPanel key={selected.serial} serial={selected.serial} />
                </div>
              ) : (
                <div className="card flex-1">
                  {/* Jog controls placeholder */}
                  <div className="bg-surface-2 rounded-lg p-6 text-center mb-4">
                    <Joystick className="mx-auto text-gray-500 mb-2" size={40} />
                    <p className="text-sm text-gray-400">Jog controls</p>
                    <p className="text-xs text-gray-600 mt-1">
                      6-axis jog, speed control, home, and diagnostics — coming soon via gRPC-over-MQTT.
                    </p>
                  </div>

                  {/* Device info */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="bg-surface-2 rounded-lg px-3 py-2">
                      <div className="text-gray-500">Firmware</div>
                      <div className="font-mono text-gray-300">v{selected.firmware_version}</div>
                    </div>
                    <div className="bg-surface-2 rounded-lg px-3 py-2">
                      <div className="text-gray-500">Model</div>
                      <div className="font-mono text-gray-300">v{selected.model_version}</div>
                    </div>
                    <div className="bg-surface-2 rounded-lg px-3 py-2">
                      <div className="text-gray-500">Cycles</div>
                      <div className="font-mono text-gray-300">{selected.cycle_count.toLocaleString()}</div>
                    </div>
                    <div className="bg-surface-2 rounded-lg px-3 py-2">
                      <div className="text-gray-500">Uptime</div>
                      <div className="font-mono text-gray-300">{selected.uptime_hours}h</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Safety callout */}
              <div className="mt-3 flex items-start gap-2 bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
                <AlertTriangle size={12} className="text-red-400 mt-0.5 flex-shrink-0" />
                <span className="text-[11px] text-red-300">
                  Remote commands are subject to all device-side safety constraints.
                  E-stop is always available locally and cannot be overridden remotely.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
